import * as go from 'gojs';
import { noop } from 'vet-bones/utils';

import { GeometryString } from 'src/containers/ProjectDiagram/constants/geometries';
import { Layer } from 'src/containers/ProjectDiagram/constants/layers';
import {
  DELETED_BACKGROUND_COLOR,
  DELETED_OUTLINE_COLOR,
  DRAFT_BACKGROUND_COLOR,
  DRAFT_OUTLINE_COLOR,
  MODEL_CONCEPT_DEFAULT_COLOR,
  MODEL_CONCEPT_MAPPED_ICON_COLOR,
  MODEL_CONCEPT_MAPPED_ICON_SIZE,
  MODEL_CONCEPT_STROKE_COLOR,
  MODEL_CONCEPT_STROKE_WIDTH,
  NODE_FONT_COLOR,
  NODE_FONT_FACE,
  NODE_FONT_SIZE,
  NODE_FONT_WEIGHT,
  NODE_SIZE,
  NODE_TEXT_WIDTH_RATIO,
} from 'src/containers/ProjectDiagram/constants/styles';
import {
  graphObjectMouseEnter,
  graphObjectMouseLeave,
} from 'src/containers/ProjectDiagram/handlers/graphObject';
import { ExtendedNode } from 'src/containers/ProjectDiagram/templates/extensions/node';
import {
  dataBinding,
  modelBinding,
  objectBinding,
} from 'src/containers/ProjectDiagram/templates/helpers/binding';
import { isClassModified } from 'src/containers/ProjectDiagram/templates/helpers/draft';
import {
  determineColorFromSelection,
  isClassScaled,
  isClassVisible,
} from 'src/containers/ProjectDiagram/templates/helpers/styles';
import { createAttributesPanel } from 'src/containers/ProjectDiagram/templates/modelConceptAttributes';
import {
  DeletedModelConceptClass,
  ModelConceptClass,
} from 'src/containers/ProjectDiagram/types/data';
import { ModelConceptClassNode } from 'src/containers/ProjectDiagram/types/diagram';
import { Model, ModelData } from 'src/containers/ProjectDiagram/types/model';
import { getDiagramPartLabel } from 'src/containers/ProjectDiagram/utils/labels';
import {
  hasMappingsForClass,
  hasValidMappingsForClass,
} from 'src/containers/ProjectDiagram/utils/mapping/hasValidMappings';
import { softHyphenate } from 'src/containers/ProjectDiagram/utils/softHyphenate';
import { queryBuilderColorsById } from 'src/templates/styles/colors';

const GEOMETRY_INVALID_MAPPED = go.Geometry.parse(
  GeometryString.INVALID_MAPPED,
  true
);
const GEOMETRY_VALID_MAPPED = go.Geometry.parse(
  GeometryString.VALID_MAPPED,
  true
);

const $ = go.GraphObject.make;

const shouldShowMappedStyle = (modelData: ModelData, shape: go.Shape) => {
  if (modelData.draftData) {
    return false;
  }

  const data = shape.part?.data;
  const model = shape.diagram?.model;
  return Boolean(data && model && hasMappingsForClass(data)(model as Model));
};

const shouldShowValidMappedStyle = (modelData: ModelData, shape: go.Shape) => {
  if (modelData.draftData) {
    return false;
  }

  const data = shape.part?.data;
  const model = shape.diagram?.model;
  return Boolean(
    data && model && hasValidMappingsForClass(data)(model as Model)
  );
};

export const createModelConceptClassTemplate = ({
  deleted = false,

  contextMenu,
  handleClick = noop,
  handleDoubleClick = noop,
  toolTip,
}: {
  deleted?: boolean;

  contextMenu: go.HTMLInfo;
  handleClick?: NonNullable<ModelConceptClassNode['click']>;
  handleDoubleClick?: NonNullable<ModelConceptClassNode['doubleClick']>;
  toolTip: go.HTMLInfo;
}) =>
  $(
    ExtendedNode,
    go.Panel.Spot,
    {
      contextMenu,
      cursor: 'pointer',
      layerName: Layer.MODEL_CONCEPT_CLASS_BACKGROUND_LAYER,
      layoutConditions:
        // eslint-disable-next-line no-bitwise
        go.LayoutConditions.Standard & ~go.LayoutConditions.NodeSized,
      locationObjectName: 'MAIN',
      locationSpot: go.Spot.Center,
      selectionAdorned: false,
    },

    objectBinding<go.Node, go.Node>()('layerName', 'isSelected', (isSelected) =>
      isSelected
        ? Layer.MODEL_CONCEPT_CLASS_FOREGROUND_LAYER
        : Layer.MODEL_CONCEPT_CLASS_BACKGROUND_LAYER
    ),
    dataBinding<go.Node, ModelConceptClass | DeletedModelConceptClass>()(
      'position',
      'pos',
      (pos) => go.Point.parse(pos || '')
    ).makeTwoWay(go.Point.stringify),
    modelBinding<go.Shape, ModelData>()('visible', '', isClassVisible),
    modelBinding<go.Shape, ModelData>()('scale', '', isClassScaled),

    $(
      go.Panel,
      go.Panel.Spot,
      {
        doubleClick: handleDoubleClick,
        click: handleClick,
        mouseEnter: graphObjectMouseEnter,
        mouseLeave: graphObjectMouseLeave,
        toolTip,
      },
      $(
        go.Shape,
        {
          name: 'MAIN',
          figure: 'Circle',
          isPanelMain: true,
          width: NODE_SIZE,
          height: NODE_SIZE,
          fill: MODEL_CONCEPT_DEFAULT_COLOR,
          stroke: MODEL_CONCEPT_STROKE_COLOR,
          strokeWidth: MODEL_CONCEPT_STROKE_WIDTH,
          portId: '',
          fromLinkable: true,
          fromLinkableSelfNode: true,
          fromLinkableDuplicates: true,
          toLinkable: true,
          toLinkableSelfNode: true,
          toLinkableDuplicates: true,
        },
        modelBinding<go.Shape, ModelData>()('fill', '', (modelData, shape) => {
          const { value: iri } = shape.part?.data;
          const colorId = modelData.colorIdsByIri[iri];
          const defaultColor =
            (!modelData.draftData && queryBuilderColorsById[colorId]) ||
            MODEL_CONCEPT_DEFAULT_COLOR;
          return determineColorFromSelection(
            isClassModified,
            deleted ? DELETED_BACKGROUND_COLOR : defaultColor,
            deleted ? DELETED_BACKGROUND_COLOR : DRAFT_BACKGROUND_COLOR
          )(modelData, shape);
        }),
        modelBinding<go.Shape, ModelData>()(
          'stroke',
          '',
          determineColorFromSelection(
            isClassModified,
            deleted ? DELETED_OUTLINE_COLOR : MODEL_CONCEPT_STROKE_COLOR,
            deleted ? DELETED_OUTLINE_COLOR : DRAFT_OUTLINE_COLOR
          )
        )
      ),
      $(
        go.Shape, // icon
        {
          width: MODEL_CONCEPT_MAPPED_ICON_SIZE,
          height: MODEL_CONCEPT_MAPPED_ICON_SIZE,
          strokeWidth: 0,
          alignment: go.Spot.Top,
          alignmentFocus: new go.Spot(0.5, 1, 0, -100),
        },
        modelBinding<go.Shape, ModelData>()('visible', '', (modelData, shape) =>
          shouldShowMappedStyle(modelData, shape)
        ),
        modelBinding<go.Shape, ModelData>()(
          'geometry',
          '',
          (modelData, shape) =>
            shouldShowValidMappedStyle(modelData, shape)
              ? GEOMETRY_VALID_MAPPED
              : GEOMETRY_INVALID_MAPPED
        ),
        modelBinding<go.Shape, ModelData>()(
          'fill',
          '',
          determineColorFromSelection(
            isClassModified,
            MODEL_CONCEPT_MAPPED_ICON_COLOR
          )
        )
      ),
      $(
        go.TextBlock,
        {
          name: 'LABEL TEXT',
          stroke: NODE_FONT_COLOR,
          font: `${NODE_FONT_WEIGHT} ${NODE_FONT_SIZE}px ${NODE_FONT_FACE}`,
          overflow: go.TextOverflow.Ellipsis,
          wrap: go.Wrap.DesiredSize,
          width: NODE_TEXT_WIDTH_RATIO * NODE_SIZE,
          textAlign: 'center',
          maxLines: 2,
        },
        deleted
          ? dataBinding<go.TextBlock, DeletedModelConceptClass>()(
              'text',
              '',
              (data) => softHyphenate(data.text)
            )
          : modelBinding<go.TextBlock, ModelData>()(
              'text',
              'labelsByIri',
              (labelsByIri: Record<string, string> = {}, obj: go.GraphObject) =>
                softHyphenate(getDiagramPartLabel(labelsByIri, obj))
            ),
        modelBinding<go.TextBlock, ModelData>()(
          'stroke',
          '',
          determineColorFromSelection(isClassModified, NODE_FONT_COLOR)
        ),
        objectBinding<go.TextBlock, go.Node>()('scale', '', (node) =>
          node.isHighlighted || node.isSelected ? 1.2 : 1
        )
      )
    ),

    deleted ? $(go.Panel) : createAttributesPanel()
  );
